import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  List,
  Modal,
  Popover,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ReloadOutlined,
  AlertOutlined,
  TeamOutlined,
  RiseOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LabelList,
} from "recharts";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import useAnalysisSnapshots from "../../../hooks/academics/useAnalysisSnapshots";
import useAnalysisReports from "../../../hooks/academics/useAnalysisReports";
import { buildStreamComparisonMatrix, getHeatmapClassesInResponseOrder, streamFieldKey } from "./streamComparisonUtils";

const CLASS_GROUPS = ["Lower Primary", "Upper Primary"];
const SUBJECT_CATEGORIES = ["Core", "Subsidiary"];
const TREND_SCOPES = [
  { value: "ClassGroup", label: "Class Group" },
  { value: "Class", label: "Class" },
  { value: "School", label: "School" },
];
const TREND_LINE_COLORS = ["#1677ff", "#52c41a", "#fa8c16", "#722ed1", "#13c2c2", "#eb2f96"];
const TREND_COLOR_MAP = {
  "Lower Primary (Core)": "#0958d9",
  "Lower Primary (Subsidiary)": "#389e0d",
  "Upper Primary (Core)": "#ffa600",
  "Upper Primary (Subsidiary)": "#b2aa00",
};
const STD_DEV_TOOLTIP =
  "Standard deviation — how spread out student scores are around the average. Lower means scores cluster tightly, higher means more variability.";

const scoreColor = (score) => {
  if (score === null || score === undefined) return "#f0f0f0";
  const value = Number(score);
  if (value >= 80) return "#389e0d";
  if (value >= 70) return "#73d13d";
  if (value >= 60) return "#fadb14";
  if (value >= 50) return "#fa8c16";
  return "#f5222d";
};

const scoreTextColor = (score) => {
  const value = Number(score);
  return value >= 60 && value < 70 ? "#000" : "#fff";
};

const SCORE_BANDS = [
  { label: "80+", color: "#389e0d" },
  { label: "70–79", color: "#73d13d" },
  { label: "60–69", color: "#fadb14" },
  { label: "50–59", color: "#fa8c16" },
  { label: "<50", color: "#f5222d" },
  { label: "No data", color: "#f0f0f0" },
  {
    label: "Not submitted",
    color: "#fffbe6",
    swatchBorder: "1px dashed #faad14",
    swatchGlyph: <ClockCircleOutlined style={{ fontSize: 11, color: "#d48806" }} />,
  },
  { label: "No teacher", color: "#fafafa", swatchGlyph: <span style={{ fontSize: 12, color: "#bfbfbf" }}>×</span> },
];

const ScoreLegend = () => (
  <Space size={12} wrap>
    {SCORE_BANDS.map((band) => (
      <Space key={band.label} size={4}>
        <span
          style={{
            display: "inline-block",
            width: 16,
            height: 16,
            borderRadius: 3,
            background: band.color,
            border: band.swatchBorder || (band.color === "#f0f0f0" ? "1px solid #d9d9d9" : "none"),
            lineHeight: "16px",
            textAlign: "center",
          }}
        >
          {band.swatchGlyph || ""}
        </span>
        <Typography.Text style={{ fontSize: 12 }}>{band.label}</Typography.Text>
      </Space>
    ))}
  </Space>
);

const num = (value, dp = 1) => (value === null || value === undefined ? "-" : Number(value).toFixed(dp));

const DeltaIndicator = ({ delta }) => {
  if (delta === null || delta === undefined) return null;
  const value = Number(delta);
  if (value === 0) return <Tag>0.0</Tag>;
  return value > 0 ? (
    <Tag color="green">
      <ArrowUpOutlined /> {value.toFixed(1)}
    </Tag>
  ) : (
    <Tag color="red">
      <ArrowDownOutlined /> {Math.abs(value).toFixed(1)}
    </Tag>
  );
};

const BandDistribution = ({ distribution }) => {
  if (!distribution || Object.keys(distribution).length === 0)
    return <Typography.Text type="secondary">No band data</Typography.Text>;
  return (
    <Space direction="vertical" size={2}>
      {Object.entries(distribution).map(([band, count]) => (
        <div key={band}>
          <Tag>{band}</Tag> {count}
        </div>
      ))}
    </Space>
  );
};

const SummaryCard = ({ title, snapshot, icon }) => (
  <Card size="small">
    <Statistic
      title={title}
      value={snapshot ? num(snapshot.avgScore) : "-"}
      prefix={icon}
      suffix={snapshot && <DeltaIndicator delta={snapshot.avgScoreDelta} />}
    />
    {snapshot && (
      <Space size="large" style={{ marginTop: 8, fontSize: 12 }}>
        <Typography.Text type="secondary">
          <TeamOutlined /> {snapshot.studentCount} students
        </Typography.Text>
        <Typography.Text type="secondary">High {num(snapshot.highestScore)}</Typography.Text>
        <Typography.Text type="secondary">Low {num(snapshot.lowestScore)}</Typography.Text>
        <Typography.Text type="secondary">
          <Tooltip title={STD_DEV_TOOLTIP}>σ {num(snapshot.stdDeviation)}</Tooltip>
        </Typography.Text>
      </Space>
    )}
  </Card>
);

const AcademicAnalysisDashboard = () => {
  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();
  const {
    loading,
    fetchHeatmap,
    fetchClassGroupSummary,
    fetchSchoolWide,
    fetchStreamComparison,
    fetchStreamDrilldown,
    fetchMultiPeriodTrend,
    fetchClassSubjects,
  } = useAnalysisSnapshots();
  const { updateFlagStatus, fetchOpenFlags } = useAnalysisReports();

  const [acadyrID, setAcadyrID] = useState(null);
  const [termID, setTermID] = useState(null);
  const [classGroup, setClassGroup] = useState(null);
  const [subjectCategory, setSubjectCategory] = useState(null);

  const [schoolWide, setSchoolWide] = useState(null);
  const [classGroupSummary, setClassGroupSummary] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [curriculum, setCurriculum] = useState([]);
  const [trendPeriods, setTrendPeriods] = useState([]);
  const [trendScope, setTrendScope] = useState("ClassGroup");
  const [compareYearID, setCompareYearID] = useState(null);
  const [multiTrend, setMultiTrend] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openFlags, setOpenFlags] = useState([]);

  const [streamComparison, setStreamComparison] = useState({
    open: false,
    context: null,
    response: null,
    drilldown: null,
    loading: false,
  });

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
  }, [fetchAcademicYears, fetchTerms]);

  const sortedTerms = useMemo(
    () => [...terms].sort((a, b) => String(a.start || "").localeCompare(String(b.start || ""))),
    [terms],
  );

  const immediatePrevious = useMemo(() => {
    if (!acadyrID || !termID || sortedTerms.length === 0) return null;
    const idx = sortedTerms.findIndex((t) => t.id === termID);
    if (idx > 0) {
      const prev = sortedTerms[idx - 1];
      return prev ? { termID: prev.id, acadyrID } : null;
    }
    const orderedYears = [...academicYears].sort((a, b) =>
      String(a.acadyr).localeCompare(String(b.acadyr), undefined, { numeric: true }),
    );
    const yearIdx = orderedYears.findIndex((y) => y.id === acadyrID);
    if (yearIdx > 0) {
      const prevYear = orderedYears[yearIdx - 1];
      const lastTerm = sortedTerms[sortedTerms.length - 1];
      return lastTerm ? { termID: lastTerm.id, acadyrID: prevYear.id } : null;
    }
    return null;
  }, [acadyrID, termID, sortedTerms, academicYears]);

  useEffect(() => {
    if (!acadyrID || !termID) return;
    setCompareYearID(null);
    setTrendPeriods(immediatePrevious ? [`${immediatePrevious.termID}:${immediatePrevious.acadyrID}`] : []);
  }, [acadyrID, termID, immediatePrevious]);

  const effectivePeriods = useMemo(() => {
    if (!termID || !acadyrID) return [];
    const baseKey = `${termID}:${acadyrID}`;
    const others = trendPeriods.filter((p) => p !== baseKey);
    return [...others, baseKey];
  }, [trendPeriods, termID, acadyrID]);

  const load = useCallback(async () => {
    if (!acadyrID || !termID) return;
    const base = { termID, acadyrID };
    const [school, groups, cells, flags, offered] = await Promise.all([
      fetchSchoolWide(base),
      fetchClassGroupSummary(base),
      fetchHeatmap({ ...base, classGroup, subjectCategory }),
      fetchOpenFlags(base),
      fetchClassSubjects(),
    ]);
    setSchoolWide(school);
    setClassGroupSummary(groups);
    setHeatmap(cells);
    setCurriculum(offered);
    setOpenFlags(flags);
  }, [
    acadyrID,
    termID,
    classGroup,
    subjectCategory,
    fetchSchoolWide,
    fetchClassGroupSummary,
    fetchHeatmap,
    fetchOpenFlags,
    fetchClassSubjects,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!acadyrID || !termID || effectivePeriods.length === 0) return;
    let active = true;
    const trendParams = {
      scopeType: trendScope,
      classGroup: trendScope === "School" ? undefined : classGroup,
      subjectCategory: trendScope === "School" ? undefined : subjectCategory,
    };
    fetchMultiPeriodTrend(
      { periods: effectivePeriods.join(","), termID, acadyrID, ...trendParams },
      { silentLoading: true },
    ).then((data) => {
      if (!active) return;
      setMultiTrend(data);
    });
    return () => {
      active = false;
    };
  }, [effectivePeriods, termID, acadyrID, trendScope, classGroup, subjectCategory, refreshKey, fetchMultiPeriodTrend]);

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
    load();
  };

  const openStreamComparison = useCallback(
    async (context) => {
      setStreamComparison({ open: true, context, response: null, drilldown: null, loading: true });
      const params = {
        acadyrID,
        termID,
        classID: context.classID,
        subjectID: context.subjectID,
      };
      const [response, drilldown] = await Promise.all([fetchStreamComparison(params), fetchStreamDrilldown(params)]);
      setStreamComparison({ open: true, context, response, drilldown, loading: false });
    },
    [acadyrID, termID, fetchStreamComparison, fetchStreamDrilldown],
  );

  const curriculumLoaded = curriculum.length > 0;
  const offeredClassSubjects = useMemo(() => {
    const set = new Set();
    curriculum.forEach((assignment) => {
      if (assignment?.classID == null || assignment?.subjectID == null) return;
      set.add(`${assignment.classID}|${assignment.subjectID}`);
    });
    return set;
  }, [curriculum]);

  const heatmapTable = useMemo(() => {
    const subjects = [];
    const classes = getHeatmapClassesInResponseOrder(heatmap);
    const cellMap = {};

    heatmap.forEach((snap) => {
      if (!subjects.some((s) => s.id === snap.subjectID)) {
        subjects.push({ id: snap.subjectID, name: snap.subjectName, category: snap.subjectCategory });
      }
      cellMap[`${snap.classID}|${snap.subjectID}`] = snap;
    });

    subjects.sort((a, b) =>
      a.category === b.category ? a.name.localeCompare(b.name) : a.category.localeCompare(b.category),
    );

    const columns = [
      {
        title: "Class",
        dataIndex: "name",
        key: "class",
        fixed: "left",
        width: 110,
        render: (name, row) => (
          <Tooltip title="Compare all subjects across this class's streams">
            <Button type="link" onClick={() => openStreamComparison({ classID: row.id, className: row.name })}>
              {name}
            </Button>
          </Tooltip>
        ),
      },
      ...subjects.map((subject) => ({
        title: (
          <Tooltip title={subject.category}>
            <span>{subject.name}</span>
          </Tooltip>
        ),
        key: subject.id,
        width: 110,
        align: "center",
        render: (_, row) => {
          const key = `${row.id}|${subject.id}`;
          const snap = cellMap[key];
          if (!snap || snap.avgScore === null || snap.avgScore === undefined) {
            if (curriculumLoaded && offeredClassSubjects.has(key)) {
              return (
                <Tooltip title="Offered — no scores submitted for this term">
                  <div
                    style={{
                      background: "#fffbe6",
                      borderTop: "1px dashed #faad14",
                      borderBottom: "1px dashed #faad14",
                      padding: "8px 0",
                      color: "#d48806",
                      fontSize: 12,
                    }}
                  >
                    <ClockCircleOutlined />
                  </div>
                </Tooltip>
              );
            }
            if (curriculumLoaded) {
              return (
                <Tooltip title="No subject teacher assigned for this class">
                  <div style={{ background: "#fafafa", padding: "8px 0", color: "#bfbfbf", fontSize: 14 }}>×</div>
                </Tooltip>
              );
            }
            return <div style={{ background: "#fafafa", padding: "8px 0" }}>-</div>;
          }
          return (
            <Popover
              title={`${snap.className} — ${snap.subjectName}`}
              content={
                <Space direction="vertical" size={4}>
                  <div>
                    Students: {snap.studentCount}{" "}
                    {snap.incompleteCount > 0 && <Tag color="orange">{snap.incompleteCount} incomplete</Tag>}
                  </div>
                  <div>
                    High {num(snap.highestScore)} · Low {num(snap.lowestScore)} ·{" "}
                    <Tooltip title={STD_DEV_TOOLTIP}>σ {num(snap.stdDeviation)}</Tooltip>
                  </div>
                  <BandDistribution distribution={snap.scoreBandDistribution} />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Click cell to compare streams
                  </Typography.Text>
                </Space>
              }
            >
              <div
                onClick={() => openStreamComparison(snap)}
                style={{
                  background: scoreColor(snap.avgScore),
                  color: scoreTextColor(snap.avgScore),
                  padding: "8px 0",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                {num(snap.avgScore)}
                {snap.avgScoreDelta !== null && snap.avgScoreDelta !== undefined && (
                  <span style={{ fontSize: 10, marginLeft: 4 }}>
                    {Number(snap.avgScoreDelta) >= 0 ? "▲" : "▼"}
                    {Math.abs(Number(snap.avgScoreDelta)).toFixed(1)}
                  </span>
                )}
              </div>
            </Popover>
          );
        },
      })),
    ];

    return { columns, rows: classes };
  }, [heatmap, openStreamComparison, curriculumLoaded, offeredClassSubjects]);

  const streamComparisonTable = useMemo(() => {
    const matrix = buildStreamComparisonMatrix(streamComparison.response);
    const columns = [
      {
        title: "Subject",
        dataIndex: "subjectName",
        key: "subjectName",
        fixed: "left",
        width: 160,
      },
      {
        title: "Category",
        dataIndex: "subjectCategory",
        key: "subjectCategory",
        width: 110,
        render: (category) => (category ? <Tag>{category}</Tag> : "-"),
      },
      ...matrix.streams.map((stream) => ({
        title: stream.streamName,
        dataIndex: streamFieldKey(stream.streamID),
        key: streamFieldKey(stream.streamID),
        width: 150,
        align: "center",
        render: (snapshot) =>
          snapshot ? (
            <Space direction="vertical" size={0}>
              <Typography.Text strong>{num(snapshot.avgScore)}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <Tooltip title={STD_DEV_TOOLTIP}>σ {num(snapshot.stdDeviation)}</Tooltip> · {snapshot.studentCount ?? 0}{" "}
                students
              </Typography.Text>
            </Space>
          ) : (
            "-"
          ),
      })),
    ];

    return { ...matrix, columns };
  }, [streamComparison.response]);

  const streamDrilldownTable = useMemo(() => {
    const rows = Array.isArray(streamComparison.drilldown) ? streamComparison.drilldown : [];
    const showSubjects = !streamComparison.context?.subjectID;
    const columns = [
      {
        title: "Stream",
        dataIndex: "streamName",
        key: "streamName",
        fixed: "left",
        width: 120,
      },
      ...(showSubjects
        ? [
            {
              title: "Subject",
              dataIndex: "subjectName",
              key: "subjectName",
              fixed: "left",
              width: 140,
              render: (subjectName) => subjectName || "-",
            },
            {
              title: "Category",
              dataIndex: "subjectCategory",
              key: "subjectCategory",
              width: 110,
              render: (category) => (category ? <Tag>{category}</Tag> : "-"),
            },
          ]
        : []),
      {
        title: "Students",
        dataIndex: "studentCount",
        key: "studentCount",
        width: 90,
        align: "center",
      },
      {
        title: "Incomplete",
        dataIndex: "incompleteCount",
        key: "incompleteCount",
        width: 100,
        align: "center",
        render: (incompleteCount) =>
          incompleteCount > 0 ? <Tag color="orange">{incompleteCount} incomplete</Tag> : (incompleteCount ?? 0),
      },
      {
        title: "Avg",
        dataIndex: "avgScore",
        key: "avgScore",
        width: 80,
        align: "center",
        render: (avgScore) => (
          <div
            style={{
              background: scoreColor(avgScore),
              color: scoreTextColor(avgScore),
              borderRadius: 4,
              padding: "2px 0",
              fontWeight: 600,
            }}
          >
            {num(avgScore)}
          </div>
        ),
      },
      {
        title: "Median",
        dataIndex: "medianScore",
        key: "medianScore",
        width: 80,
        align: "center",
        render: (medianScore) => num(medianScore),
      },
      {
        title: "High",
        dataIndex: "highestScore",
        key: "highestScore",
        width: 70,
        align: "center",
        render: (highestScore) => num(highestScore),
      },
      {
        title: "Low",
        dataIndex: "lowestScore",
        key: "lowestScore",
        width: 70,
        align: "center",
        render: (lowestScore) => num(lowestScore),
      },
      {
        title: <Tooltip title={STD_DEV_TOOLTIP}>σ</Tooltip>,
        dataIndex: "stdDeviation",
        key: "stdDeviation",
        width: 70,
        align: "center",
        render: (stdDeviation) => num(stdDeviation),
      },
      {
        title: (
          <Tooltip title="Average score change vs the previous term for this stream. Positive means improvement, negative means a decline.">
            Δ
          </Tooltip>
        ),
        dataIndex: "avgScoreDelta",
        key: "avgScoreDelta",
        width: 100,
        align: "center",
        render: (avgScoreDelta) => <DeltaIndicator delta={avgScoreDelta} />,
      },
    ];

    return { columns, rows };
  }, [streamComparison.drilldown, streamComparison.context?.subjectID]);

  const baseLabel = useMemo(() => {
    const baseAcademicYear = academicYears.find((y) => y.id === acadyrID)?.acadyr;
    const selectedTerm = terms.find((t) => t.id === termID);
    return selectedTerm ? `${baseAcademicYear ? `${baseAcademicYear} · ` : ""}${selectedTerm.name}` : null;
  }, [academicYears, terms, acadyrID, termID]);

  const periodOptions = useMemo(() => {
    const byYear = new Map();
    academicYears.forEach((y) => {
      if (!byYear.has(y.id)) byYear.set(y.id, { value: y.id, label: y.acadyr, options: [] });
      terms.forEach((t) => {
        byYear.get(y.id).options.push({ value: `${t.id}:${y.id}`, label: t.name });
      });
    });
    return [...byYear.values()];
  }, [academicYears, terms]);

  const periodLabelMap = useMemo(() => {
    const map = new Map();
    periodOptions.forEach((group) => group.options.forEach((o) => map.set(o.value, `${group.label} · ${o.label}`)));
    return map;
  }, [periodOptions]);

  const activeCompareYear = compareYearID ?? (trendPeriods[0] ? trendPeriods[0].split(":")[1] : null);
  const termOptions = useMemo(() => {
    if (!activeCompareYear) return [];
    const baseKey = `${termID}:${acadyrID}`;
    return terms
      .filter((t) => `${t.id}:${activeCompareYear}` !== baseKey)
      .map((t) => ({ value: `${t.id}:${activeCompareYear}`, label: t.name }));
  }, [terms, activeCompareYear, termID, acadyrID]);
  const selectedTermKeys = trendPeriods.filter((p) => p && p.endsWith(`:${activeCompareYear}`));

  const handleTermSelectionChange = (keys) => {
    setTrendPeriods((prev) => [...prev.filter((p) => !p.endsWith(`:${activeCompareYear}`)), ...(keys || [])]);
  };

  const multiTrendChart = useMemo(() => {
    if (!multiTrend || !Array.isArray(multiTrend.periods) || multiTrend.periods.length === 0) {
      return { rows: [], periods: [], periodKeys: [] };
    }
    const periodKeys = multiTrend.periods.map((_, i) => `period_${i}`);
    const periods = multiTrend.periods.map((p) => `${p.academicYear ? `${p.academicYear} · ` : ""}${p.termName}`);
    const byScope = new Map();
    const scopes = [];
    multiTrend.rows
      .filter((r) => !subjectCategory || r.subjectCategory === subjectCategory)
      .forEach((row) => {
        if (!byScope.has(row.scopeLabel)) {
          scopes.push(row.scopeLabel);
          byScope.set(row.scopeLabel, new Map());
        }
        const catMap = byScope.get(row.scopeLabel);
        if (!catMap.has(row.subjectCategory)) {
          catMap.set(row.subjectCategory, new Map());
        }
        const periodMap = catMap.get(row.subjectCategory);
        const key = `period_${row.periodOrder}`;
        if (!periodMap.has(key)) periodMap.set(key, []);
        periodMap.get(key).push(row);
      });
    const pickRepresentative = (rowsForPeriod) => {
      const aggregate = rowsForPeriod.find((r) => r.subjectName == null && r.avgScore != null);
      if (aggregate) return aggregate;
      return rowsForPeriod.find((r) => r.avgScore != null) || null;
    };
    const CAT_ORDER = { Core: 0, Subsidiary: 1 };
    const rows = [];
    scopes.forEach((scope) => {
      const cats = [...byScope.get(scope).entries()].sort((a, b) => {
        const ao = CAT_ORDER[a[0]] != null ? CAT_ORDER[a[0]] : 2;
        const bo = CAT_ORDER[b[0]] != null ? CAT_ORDER[b[0]] : 2;
        return ao - bo;
      });
      cats.forEach(([category, periodMap]) => {
        const row = { name: `${scope} (${category})` };
        periodKeys.forEach((key) => {
          const rep = pickRepresentative(periodMap.get(key) || []);
          row[key] = rep ? Number(rep.avgScore) : null;
          row[`${key}_delta`] = rep && rep.avgScoreDelta != null ? Number(rep.avgScoreDelta) : null;
        });
        rows.push(row);
      });
    });
    return { rows, periods, periodKeys };
  }, [multiTrend, subjectCategory]);

  const handleFlagUpdate = async (flag, flagStatus) => {
    const updated = await updateFlagStatus(flag.id, flagStatus);
    if (!updated) return;
    if (flagStatus === "Resolved") {
      setOpenFlags((prev) => prev.filter((f) => f.id !== flag.id));
    } else {
      setOpenFlags((prev) => prev.map((f) => (f.id === flag.id ? { ...f, flagStatus } : f)));
    }
  };

  const groupSnapshot = (group, category) =>
    classGroupSummary.find((s) => s.classGroup === group && s.subjectCategory === category);

  const filtersReady = acadyrID && termID;

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Card size="small">
        <Space wrap>
          <Select
            placeholder="Academic Year"
            style={{ width: 160 }}
            value={acadyrID}
            onChange={setAcadyrID}
            options={academicYears.map((y) => ({ value: y.id, label: y.acadyr }))}
          />
          <Select
            placeholder="Term"
            style={{ width: 130 }}
            value={termID}
            onChange={setTermID}
            options={terms.map((t) => ({ value: t.id, label: t.name }))}
          />
          <Select
            placeholder="Class Group"
            allowClear
            style={{ width: 160 }}
            value={classGroup}
            onChange={setClassGroup}
            options={CLASS_GROUPS.map((g) => ({ value: g, label: g }))}
          />
          <Select
            placeholder="Subject Category"
            allowClear
            style={{ width: 160 }}
            value={subjectCategory}
            onChange={setSubjectCategory}
            options={SUBJECT_CATEGORIES.map((c) => ({ value: c, label: c }))}
          />
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} disabled={!filtersReady}>
            Refresh
          </Button>
        </Space>
      </Card>

      {!filtersReady && (
        <Alert type="info" showIcon message="Select an academic year and term to load the analysis dashboard." />
      )}

      {filtersReady && (
        <>
          {schoolWide == null && (
            <Alert
              type="warning"
              showIcon
              message="No data found for this year and term."
              description="Run an aggregation to populate the dashboard."
            />
          )}
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <SummaryCard title="School-Wide Average" snapshot={schoolWide} icon={<RiseOutlined />} />
            </Col>
            {CLASS_GROUPS.map((group) =>
              SUBJECT_CATEGORIES.map((category) => {
                const snap = groupSnapshot(group, category);
                if (!snap) return null;
                return (
                  <Col xs={24} md={8} key={`${group}-${category}`}>
                    <SummaryCard title={`${group} — ${category}`} snapshot={snap} />
                  </Col>
                );
              }),
            )}
          </Row>

          <Card
            title="Class × Subject Heatmap"
            size="small"
            extra={
              <Space direction="vertical" size={4} align="end">
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Click a class for the full comparison, or a score for one subject. Amber = offered but not yet
                  submitted; grey × = no subject teacher assigned.
                </Typography.Text>
                <ScoreLegend />
              </Space>
            }
          >
            {heatmap.length === 0 ? (
              <Empty description="No snapshot data for this term. Run an aggregation first." />
            ) : (
              <Table
                rowKey="id"
                size="small"
                loading={loading}
                columns={heatmapTable.columns}
                dataSource={heatmapTable.rows}
                pagination={false}
                scroll={{ x: "max-content" }}
              />
            )}
          </Card>

          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card
                title="Term-over-Term Trend"
                size="small"
                extra={
                  <Space wrap>
                    <Select
                      placeholder="Scope"
                      size="small"
                      style={{ width: 130 }}
                      value={trendScope}
                      onChange={setTrendScope}
                      options={TREND_SCOPES}
                    />
                    <Select
                      placeholder="Compare Year"
                      allowClear
                      size="small"
                      style={{ width: 130 }}
                      value={activeCompareYear}
                      onChange={setCompareYearID}
                      options={academicYears.map((y) => ({ value: String(y.id), label: y.acadyr }))}
                    />
                    <Select
                      mode="multiple"
                      placeholder={activeCompareYear ? "Compare Term(s)" : "Select a year first"}
                      allowClear
                      size="small"
                      style={{ minWidth: 180 }}
                      value={selectedTermKeys}
                      onChange={handleTermSelectionChange}
                      options={termOptions}
                      maxTagCount="responsive"
                      maxTagTextLength={14}
                    />
                  </Space>
                }
              >
                {!multiTrend ? (
                  <Alert
                    type="warning"
                    showIcon
                    message={`No data found for the selected ${baseLabel || "year and term"}.`}
                    description={trendPeriods.length ? "The selected comparison periods also have no data." : undefined}
                  />
                ) : multiTrendChart.rows.length === 0 ? (
                  <Empty description="No trend data available for the selected periods." />
                ) : (
                  <>
                    <ResponsiveContainer
                      width="100%"
                      height={Math.max(460, multiTrendChart.periodKeys.length * 44 + 320)}
                    >
                      <BarChart
                        data={multiTrendChart.rows}
                        barGap={3}
                        barCategoryGap={12}
                        margin={{ left: 50, right: 140, top: 32, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" interval={0} angle={20} textAnchor="start" height={110} />
                        <YAxis domain={[0, (dataMax) => dataMax + 12]} />
                        <RechartsTooltip />
                        <Legend />
                        {multiTrendChart.periodKeys.map((key, index) => {
                          const periodLabel = multiTrendChart.periods[index];
                          const isBase = periodLabel === baseLabel;
                          return (
                            <Bar
                              key={key}
                              dataKey={key}
                              name={periodLabel}
                              fill={TREND_COLOR_MAP[periodLabel] || TREND_LINE_COLORS[index % TREND_LINE_COLORS.length]}
                              maxBarSize={40}
                              stroke={isBase ? "#1890ff" : "none"}
                              strokeWidth={isBase ? 2 : 1}
                            >
                              {index > 0 && (
                                <LabelList
                                  dataKey={`${key}_delta`}
                                  position="top"
                                  formatter={(d) =>
                                    d === null || d === undefined
                                      ? ""
                                      : `${Number(d) >= 0 ? "+" : "−"}${Math.abs(Number(d)).toFixed(1)}`
                                  }
                                  style={{ fontSize: 11 }}
                                />
                              )}
                            </Bar>
                          );
                        })}
                      </BarChart>
                    </ResponsiveContainer>
                    {trendPeriods.length === 0 && (
                      <Typography.Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 13 }}>
                        Showing the selected {baseLabel}. Select one or more periods (Year · Term) to compare with other
                        terms.
                      </Typography.Text>
                    )}
                    {multiTrend.missing && multiTrend.missing.length > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                        message={`The ${multiTrend.missing.map((p) => periodLabelMap.get(p) || p).join(", ")} has no previous delta to compute against, so it could not be shown.`}
                      />
                    )}
                    <Typography.Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 13 }}>
                      {multiTrendChart.periodKeys.length > 1
                        ? `Average scores per ${trendScope === "Class" ? "class" : trendScope === "School" ? "school" : "class group"} and subject category, one bar per period; the tag on each bar shows the difference from the previous period, and the blue-outlined bar is the selected base term.`
                        : `Average scores per ${trendScope === "Class" ? "class" : trendScope === "School" ? "school" : "class group"} and subject category for the selected period.`}
                    </Typography.Text>
                  </>
                )}
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card
                title={
                  <>
                    <AlertOutlined /> Open Flags
                  </>
                }
                size="small"
              >
                {openFlags.length === 0 ? (
                  <Empty description="No open flags for this term." />
                ) : (
                  <List
                    size="small"
                    dataSource={openFlags}
                    renderItem={(flag) => (
                      <List.Item
                        actions={[
                          flag.flagStatus === "Open" && (
                            <Button size="small" onClick={() => handleFlagUpdate(flag, "Acknowledged")}>
                              Acknowledge
                            </Button>
                          ),
                          <Button size="small" type="primary" onClick={() => handleFlagUpdate(flag, "Resolved")}>
                            Resolve
                          </Button>,
                        ].filter(Boolean)}
                      >
                        <List.Item.Meta
                          title={
                            <Space>
                              <Tag color={flag.actionType === "Flag" ? "red" : "orange"}>{flag.actionType}</Tag>
                              {flag.severity && <Tag color="volcano">{flag.severity}</Tag>}
                              {flag.metricReference && <Typography.Text code>{flag.metricReference}</Typography.Text>}
                            </Space>
                          }
                          description={
                            <>
                              {flag.note && <div>{flag.note}</div>}
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {flag.actorName || "Unknown"} · Report #{flag.reportID}
                              </Typography.Text>
                            </>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}

      <Modal
        title={
          streamComparison.context?.subjectID
            ? `Stream Comparison — ${streamComparison.response?.className || streamComparison.context?.className || ""} · ${streamComparison.context?.subjectName || ""}`
            : `All-Subject Stream Comparison — ${streamComparison.response?.className || streamComparison.context?.className || ""}`
        }
        open={streamComparison.open}
        onCancel={() =>
          setStreamComparison({ open: false, context: null, response: null, drilldown: null, loading: false })
        }
        footer={null}
        width={1100}
        destroyOnClose
      >
        <Tabs
          defaultActiveKey="comparison"
          items={[
            {
              key: "comparison",
              label: "Stream Comparison",
              children:
                streamComparisonTable.rows.length === 0 && !streamComparison.loading ? (
                  <Empty description="No stream comparison data is available for this class." />
                ) : (
                  <Table
                    rowKey="subjectID"
                    size="small"
                    loading={streamComparison.loading}
                    columns={streamComparisonTable.columns}
                    dataSource={streamComparisonTable.rows}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                  />
                ),
            },
            {
              key: "drilldown",
              label: "Stream Drilldown",
              children:
                streamDrilldownTable.rows.length === 0 && !streamComparison.loading ? (
                  <Empty description="No stream drilldown data is available for this class." />
                ) : (
                  <Table
                    rowKey="id"
                    size="small"
                    loading={streamComparison.loading}
                    columns={streamDrilldownTable.columns}
                    dataSource={streamDrilldownTable.rows}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                  />
                ),
            },
          ]}
        />
      </Modal>
    </Space>
  );
};

export default AcademicAnalysisDashboard;
